import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, CheckCircle2, History, PencilLine, Wallet } from "lucide-react";
import { api, errMsg, fmtINR, fmtDateTime } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const MIN_REASON = 5;

/** Endpoints for the two callers: tenant (Municipal Admin portal) or platform (Super Admin portal). */
export const demandEndpoints = {
  tenant: (code, demandId) => ({ update: `/t/${code}/demands/${demandId}`, history: `/t/${code}/demands/${demandId}/corrections` }),
  platform: (code, demandId) => ({ update: `/platform/municipalities/${code}/demands/${demandId}`, history: `/platform/municipalities/${code}/demands/${demandId}/corrections` }),
};

/**
 * Phase 188 — correct a demand (Arrear / Current / Penalty / Rebate) that may already be partly or fully paid.
 * Shows what will happen before saving: new due, paid so far, projected balance or overpayment credit. Reason is mandatory.
 * On save the server re-fits the payments (excess settles the property's other dues first, remainder = credit) and writes
 * an audit row; `onSaved(result)` lets the caller reload the property.
 */
/** `extraBody` (Phase 190): extra fields sent with the correction, e.g. `{ flag_id }` when correcting from Flagged Payments. */
export function DemandEditDialog({ code, demand, pin, open, onOpenChange, onSaved, mode = "tenant", extraBody = null }) {
  const [form, setForm] = useState({ arrear_amount: "", current_amount: "", penalty_amount: "", rebate_amount: "" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const ep = useMemo(() => (demand ? demandEndpoints[mode](code, demand.id) : null), [code, demand, mode]);

  useEffect(() => {
    if (!open || !demand) return;
    setForm({
      arrear_amount: String(r2(demand.arrear_amount)), current_amount: String(r2(demand.current_amount)),
      penalty_amount: String(r2(demand.penalty_amount)), rebate_amount: String(r2(demand.rebate_amount)),
    });
    setReason(""); setBusy(false); setHistory(null); setShowHistory(false);
    if (ep && (demand.correction_count || 0) > 0) {
      api.get(ep.history).then((r) => setHistory(r.data.rows || [])).catch(() => setHistory([]));
    }
  }, [open, demand, ep]);

  if (!demand) return null;

  const num = (k) => { const v = parseFloat(form[k]); return Number.isFinite(v) ? r2(v) : NaN; };
  const arrear = num("arrear_amount"), current = num("current_amount"), penalty = num("penalty_amount"), rebate = num("rebate_amount");
  const valid = [arrear, current, penalty, rebate].every((v) => Number.isFinite(v) && v >= 0);
  const total = valid ? r2(arrear + current) : NaN;
  const due = valid ? Math.max(r2(total + penalty - rebate), 0) : NaN;
  const beforeTotal = r2(demand.total_demand);
  const beforeDue = Math.max(r2(beforeTotal + r2(demand.penalty_amount) - r2(demand.rebate_amount)), 0);
  const paid = r2(demand.paid_amount);
  const changed = valid && (Math.abs(arrear - r2(demand.arrear_amount)) > 0.004 || Math.abs(current - r2(demand.current_amount)) > 0.004
    || Math.abs(penalty - r2(demand.penalty_amount)) > 0.004 || Math.abs(rebate - r2(demand.rebate_amount)) > 0.004);
  const rebateTooBig = valid && rebate > r2(total + penalty) + 0.004;
  const newBalance = valid ? Math.max(r2(due - paid), 0) : NaN;
  const excess = valid ? Math.max(r2(paid - due), 0) : NaN;
  const reasonOk = reason.trim().length >= MIN_REASON;
  const canSave = valid && changed && !rebateTooBig && reasonOk && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const r = await api.put(ep.update, { arrear_amount: arrear, current_amount: current, penalty_amount: penalty, rebate_amount: rebate, reason: reason.trim(), ...(extraBody || {}) });
      const d = r.data;
      const n = d.owner_notification || {};
      const ownerNote = n.enabled === false ? "Owner alerts for demand corrections are switched off (Notifications)."
        : n.enabled && (n.mobile || n.in_app) ? `Owner notified${n.mobile ? " by SMS / WhatsApp" : ""}${n.mobile && n.in_app ? " and" : ""}${n.in_app ? " in the Citizen Portal" : ""}.`
        : n.enabled ? "Owner not notified — no mobile number on record." : "";
      toast.success(`Demand corrected: ${fmtINR(beforeTotal)} → ${fmtINR(d.demand.total_demand)}`, {
        description: (d.overpayment_credit > 0.004
          ? `${fmtINR(d.overpayment_credit)} already paid over the corrected demand is now held as credit against ${pin || "the property"}${d.settled_other_demands > 0.004 ? ` (${fmtINR(d.settled_other_demands)} settled other dues)` : ""}.`
          : d.credit_applied > 0.004
            ? `${fmtINR(d.credit_applied)} of credit held on ${pin || "the property"} was applied; remaining balance on this demand is now ${fmtINR(d.balance_after)}.`
            : `Remaining balance on this demand is now ${fmtINR(d.balance_after)}.`) + (ownerNote ? ` ${ownerNote}` : ""),
      });
      onSaved?.(d);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const field = (k, label) => (
    <div className="space-y-1">
      <Label htmlFor={`demand-edit-${k}`} className="text-xs">{label}</Label>
      <Input id={`demand-edit-${k}`} type="number" inputMode="decimal" min="0" step="0.01" value={form[k]} disabled={busy}
        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="tabular-nums" data-testid={`demand-edit-${k}`}
        aria-invalid={!(Number.isFinite(num(k)) && num(k) >= 0)} />
      {Math.abs(num(k) - r2(demand[k])) > 0.004 && Number.isFinite(num(k)) && (
        <p className="text-[11px] text-muted-foreground tabular-nums">was {fmtINR(demand[k])}</p>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="demand-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2"><PencilLine className="h-4 w-4" /> Correct demand</DialogTitle>
          <DialogDescription>
            {demand.tax_head_name || "Demand"}{demand.fy_label ? ` · ${demand.fy_label}` : ""}{pin ? ` · ${pin}` : ""}.{" "}
            Payments already received stay as they are: money above the corrected demand settles the property's other dues first and the
            rest is held as credit, which is used automatically against whatever the property owes later; a higher demand raises the
            balance due. Receipts already issued are not reissued. The correction is recorded in the audit trail with your name, the time
            and the reason. The owner is told about the correction by SMS / WhatsApp and in the Citizen Portal (old → new demand, paid
            so far, new balance or credit) — never the reason.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {field("arrear_amount", "Arrear")}
            {field("current_amount", "Current")}
            {field("penalty_amount", "Penalty")}
            {field("rebate_amount", "Rebate")}
          </div>
          {!valid && <p className="text-xs text-destructive" data-testid="demand-edit-invalid">Enter amounts of zero or more.</p>}
          {rebateTooBig && <p className="text-xs text-destructive" data-testid="demand-edit-rebate-error">Rebate cannot exceed the demand plus penalty.</p>}

          {/* what will happen */}
          <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1.5" data-testid="demand-edit-projection">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Total demand</span>
              <span className="tabular-nums"><span className="text-muted-foreground">{fmtINR(beforeTotal)}</span> <ArrowRight className="inline h-3 w-3 mx-1" /><span className="font-medium" data-testid="demand-edit-new-total">{valid ? fmtINR(total) : "—"}</span></span>
            </div>
            {(penalty > 0 || rebate > 0 || r2(demand.penalty_amount) > 0 || r2(demand.rebate_amount) > 0) && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Amount due (with penalty − rebate)</span>
                <span className="tabular-nums"><span className="text-muted-foreground">{fmtINR(beforeDue)}</span> <ArrowRight className="inline h-3 w-3 mx-1" /><span className="font-medium" data-testid="demand-edit-new-due">{valid ? fmtINR(due) : "—"}</span></span>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Payment already received</span>
              <span className="tabular-nums font-medium" data-testid="demand-edit-paid">{fmtINR(paid)}</span>
            </div>
            {valid && (excess > 0.004 ? (
              <div className="flex items-start justify-between gap-3 rounded-md bg-primary/5 border border-primary/20 px-2.5 py-2 mt-1" data-testid="demand-edit-overpayment">
                <span className="flex items-center gap-1.5 text-primary"><Wallet className="h-4 w-4" /> Overpayment → credit against the property</span>
                <span className="tabular-nums font-semibold text-primary" data-testid="demand-edit-overpayment-amount">{fmtINR(excess)}</span>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 mt-1">
                <span className="text-muted-foreground">New remaining balance</span>
                <span className="tabular-nums font-semibold" data-testid="demand-edit-new-balance">{fmtINR(newBalance)}</span>
              </div>
            ))}
            {valid && excess > 0.004 && (
              <p className="text-[11px] text-muted-foreground">If this property has other unpaid demands the excess settles them first; only the remainder stays as credit.</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="demand-edit-reason" className="text-xs">Reason for the correction <span className="text-destructive">*</span></Label>
            <Textarea id="demand-edit-reason" rows={3} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Built-up area was recorded twice in the 2025-26 assessment; corrected as per revised measurement"
              data-testid="demand-edit-reason" aria-invalid={reason.length > 0 && !reasonOk} />
            <p className="text-[11px] text-muted-foreground">{reasonOk ? "Stored permanently in the audit trail." : `At least ${MIN_REASON} characters.`}</p>
          </div>

          {(demand.correction_count || 0) > 0 && (
            <div className="text-xs" data-testid="demand-edit-history">
              <button type="button" onClick={() => setShowHistory((v) => !v)} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" data-testid="demand-edit-history-toggle">
                <History className="h-3.5 w-3.5" /> {showHistory ? "Hide" : "Show"} {demand.correction_count} earlier correction{demand.correction_count === 1 ? "" : "s"}
              </button>
              {showHistory && (
                <ul className="mt-1.5 divide-y rounded-md border">
                  {(history || []).map((h) => (
                    <li key={h.id} className="px-2.5 py-1.5 space-y-0.5" data-testid={`demand-correction-${h.id}`}>
                      <p className="tabular-nums"><span className="text-muted-foreground">{fmtINR(h.before?.total_demand)}</span> <ArrowRight className="inline h-3 w-3 mx-0.5" /> <span className="font-medium">{fmtINR(h.after?.total_demand)}</span>
                        <span className="text-muted-foreground"> · paid {fmtINR(h.payment_received)} · {h.overpayment_credit > 0.004 ? `credit ${fmtINR(h.overpayment_credit)}` : `balance ${fmtINR(h.balance_after)}`}{h.credit_applied > 0.004 ? ` · ${fmtINR(h.credit_applied)} credit applied` : ""}</span></p>
                      <p className="text-muted-foreground">{h.actor_name}{h.actor?.role_label ? ` (${h.actor.role_label})` : ""} · {fmtDateTime(h.created_at)} — {h.reason}</p>
                    </li>
                  ))}
                  {history && history.length === 0 && <li className="px-2.5 py-1.5 text-muted-foreground">No history available.</li>}
                  {!history && <li className="px-2.5 py-1.5 text-muted-foreground">Loading…</li>}
                </ul>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              {changed ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {changed ? "Changes ready" : "Change at least one amount"}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="demand-edit-cancel">Cancel</Button>
              <Button onClick={save} disabled={!canSave} data-testid="demand-edit-save">{busy ? "Saving…" : "Save correction"}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Small "Corrected ×n" marker for demand rows. */
export const CorrectedBadge = ({ demand, testId }) => (
  (demand?.correction_count || 0) > 0 ? (
    <Badge variant="outline" className="text-[10px] font-normal gap-1 bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900"
      title={`Corrected ${demand.correction_count} time${demand.correction_count === 1 ? "" : "s"}${demand.corrected_by_name ? ` · last by ${demand.corrected_by_name}` : ""}${demand.corrected_at ? ` on ${fmtDateTime(demand.corrected_at)}` : ""}${demand.last_correction_reason ? `\nReason: ${demand.last_correction_reason}` : ""}`}
      data-testid={testId}>
      <PencilLine className="h-2.5 w-2.5" /> Corrected{demand.correction_count > 1 ? ` ×${demand.correction_count}` : ""}
    </Badge>
  ) : null
);
